import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { kmToUnit, speedUnitLabel, UnitPreference } from '../../utils/units';

export interface TripPoint {
  lat: number;
  lon: number;
  speed: number | null;
  timestamp: string;
}

export interface TripSegment {
  type: 'trasa' | 'zatrzymanie' | 'postoj';
  start_time: string;
  end_time: string;
  duration_seconds: number;
  distance_m: number;
  avg_speed_kmh: number;
  max_speed_kmh: number | null;
  start_lat: number;
  start_lon: number;
  end_lat: number;
  end_lon: number;
  points: TripPoint[];
}

const startIcon = L.divIcon({
  html: '<div style="width:12px;height:12px;background:#4caf50;border-radius:50%;border:2px solid #fff"></div>',
  iconSize: [12, 12],
  className: '',
});
const endIcon = L.divIcon({
  html: '<div style="width:12px;height:12px;background:#e63946;border-radius:50%;border:2px solid #fff"></div>',
  iconSize: [12, 12],
  className: '',
});
// Same lucide icons used in the History list (circle-pause / circle-parking),
// enlarged for visibility on the map.
const STOP_ICON_SVG: Record<'zatrzymanie' | 'postoj', string> = {
  zatrzymanie: '<circle cx="12" cy="12" r="10"></circle><line x1="10" x2="10" y1="15" y2="9"></line><line x1="14" x2="14" y1="15" y2="9"></line>',
  postoj: '<circle cx="12" cy="12" r="10"></circle><path d="M9 17V7h4a3 3 0 0 1 0 6H9"></path>',
};

const stopIcon = (type: 'zatrzymanie' | 'postoj', color: string) => L.divIcon({
  html: `
    <div style="width:40px;height:40px;background:#fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        ${STOP_ICON_SVG[type]}
      </svg>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  className: '',
});

function formatPointSpeed(speedKnots: number | null, unit: UnitPreference): string {
  if (speedKnots == null) return '—';
  return `${Math.round(kmToUnit(speedKnots * 1.852, unit))} ${speedUnitLabel(unit)}`;
}

function fmtTime(iso: string): string {
  return new Date(iso + 'Z').toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Thin out the clickable point markers so a long trip doesn't render
// hundreds of overlapping circles — the polyline still uses every point.
function sampledPoints(points: TripPoint[], maxMarkers = 45): TripPoint[] {
  if (points.length <= maxMarkers) return points;
  const step = Math.ceil(points.length / maxMarkers);
  const out: TripPoint[] = [];
  for (let i = 0; i < points.length; i += step) out.push(points[i]);
  if (out[out.length - 1] !== points[points.length - 1]) out.push(points[points.length - 1]);
  return out;
}

function FitBounds({ coords }: { coords: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length === 0) return;
    if (coords.length === 1) {
      map.setView(coords[0], 15);
      return;
    }
    map.fitBounds(L.latLngBounds(coords), { padding: [30, 30] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords.length]);
  return null;
}

interface Props {
  segment: TripSegment;
  unit: UnitPreference;
}

export default function TripSegmentMap({ segment, unit }: Props) {
  if (segment.type === 'trasa') {
    const coords: [number, number][] = segment.points.map((p) => [p.lat, p.lon]);
    const markers = sampledPoints(segment.points);
    return (
      <MapContainer center={coords[0] || [52.2297, 21.0122]} zoom={13} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <FitBounds coords={coords} />
        <Polyline positions={coords} color="#e63946" weight={3} opacity={0.8} />
        {markers.map((p) => (
          <CircleMarker
            key={p.timestamp}
            center={[p.lat, p.lon]}
            radius={5}
            pathOptions={{ color: '#fff', weight: 1, fillColor: '#e63946', fillOpacity: 0.9 }}
          >
            <Popup>
              {formatPointSpeed(p.speed, unit)}<br />
              {fmtTime(p.timestamp)}
            </Popup>
          </CircleMarker>
        ))}
        {coords.length > 0 && <Marker position={coords[0]} icon={startIcon} />}
        {coords.length > 1 && <Marker position={coords[coords.length - 1]} icon={endIcon} />}
      </MapContainer>
    );
  }

  // zatrzymanie / postoj — single stationary point
  const center: [number, number] = [segment.start_lat, segment.start_lon];
  const stopType = segment.type === 'postoj' ? 'postoj' : 'zatrzymanie';
  const color = segment.type === 'postoj' ? '#4cc9f0' : '#ffb703';
  return (
    <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <Marker position={center} icon={stopIcon(stopType, color)}>
        <Popup>
          {fmtTime(segment.start_time)} – {fmtTime(segment.end_time)}
        </Popup>
      </Marker>
    </MapContainer>
  );
}
